import type { ReactNode } from 'react'
import { THEME_OPTIONS, type ThemePreference, useTheme } from '@/features/theme/use-theme'

/*
 * Three segments rather than a two-state switch, because "follow the system" is
 * a real answer and not the absence of one — collapsing it into a toggle would
 * silently pin whichever theme happened to be showing when someone tapped it.
 *
 * Line art at 14px, matching the sort caret and popover chevron: the control
 * sits in the sidebar footer next to the API dot, where a filled icon would
 * outweigh the status it stands beside.
 */
const ICONS: Record<ThemePreference, ReactNode> = {
  system: (
    <>
      <rect x="1.6" y="2.6" width="12.8" height="8.6" rx="1" />
      <path d="M5.5 14h5M8 11.2V14" />
    </>
  ),
  light: (
    <>
      <circle cx="8" cy="8" r="3.1" />
      <path d="M8 1v1.7M8 13.3V15M15 8h-1.7M2.7 8H1M12.9 3.1l-1.2 1.2M4.3 11.7l-1.2 1.2M12.9 12.9l-1.2-1.2M4.3 4.3L3.1 3.1" />
    </>
  ),
  dark: <path d="M13.4 9.7A5.9 5.9 0 0 1 6.3 2.6a5.9 5.9 0 1 0 7.1 7.1Z" />,
}

function ThemeIcon({ preference }: { preference: ThemePreference }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-3.5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {ICONS[preference]}
    </svg>
  )
}

/**
 * Segmented theme control.
 *
 * `showLabels` is the difference between the rail — where width is scarce and
 * the icons carry it — and the Einstellungen page, where the setting is the
 * subject of the page and deserves words.
 */
export function ThemeToggle({ showLabels = false }: { showLabels?: boolean }) {
  const { preference, setPreference } = useTheme()

  return (
    // `w-fit` because the rail lays its footer out as a column: without it the
    // control is stretched to the full 240px and the segments float in a box
    // three times their width.
    // <fieldset> brings its own box model and legend handling; this is three
    // buttons in a row, and role="group" is exactly what it is.
    // biome-ignore lint/a11y/useSemanticElements: see above
    <div role="group" aria-label="Darstellung" className="border-line inline-flex w-fit border">
      {THEME_OPTIONS.map((option) => {
        const isActive = preference === option.value

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => setPreference(option.value)}
            aria-pressed={isActive}
            // Icon-only segments have no accessible name of their own, and the
            // title doubles as the pointer tooltip.
            aria-label={showLabels ? undefined : option.label}
            title={option.label}
            className={[
              'border-line flex items-center gap-1.5 border-l px-2 py-1.5 text-[11px] transition-colors first:border-l-0',
              isActive
                ? 'bg-brand-soft text-brand font-medium'
                : 'text-faint hover:text-ink hover:bg-surface',
            ].join(' ')}
          >
            <ThemeIcon preference={option.value} />
            {showLabels && <span>{option.label}</span>}
          </button>
        )
      })}
    </div>
  )
}
